import clsx from "clsx";
import { ReactNode } from "react";

export enum ProgressCircleSize {
  XS = "xs",
  SM = "sm",
  MD = "md",
  LG = "lg",
}

export enum ProgressCircleUnit {
  NONE = "",
  PERCENTAGE = "%",
  KB = "KB",
}

export enum ProgressCircleColor {
  GREEN = "green",
  AMBER = "amber",
  RED = "red",
  PURPLE = "purple",
}

type SizeConfig = {
  diameter: number;
  strokeWidth: number;
  labelClassName: string;
};

const SIZE_CONFIG: Record<ProgressCircleSize, SizeConfig> = {
  xs: {
    diameter: 32,
    strokeWidth: 4,
    labelClassName: "text-[10px]",
  },
  sm: {
    diameter: 68,
    strokeWidth: 4,
    labelClassName: "text-sm",
  },
  md: {
    diameter: 96,
    strokeWidth: 8,
    labelClassName: "text-sm",
  },
  lg: {
    diameter: 120,
    strokeWidth: 8,
    labelClassName: "text-lg",
  },
};

export type ProgressCircleProps = {
  infinite?: boolean;
  value?: number;
  unit?: ProgressCircleUnit;
  size?: ProgressCircleSize;
  color?: ProgressCircleColor;
  showLabel?: boolean;
  renderLabel?: (value: number) => ReactNode;
  ariaLabel?: string;
  testId?: string;
};

export const ProgressCircle = ({
  infinite = false,
  value = 0,
  unit = ProgressCircleUnit.PERCENTAGE,
  size = ProgressCircleSize.MD,
  color = ProgressCircleColor.GREEN,
  showLabel,
  ariaLabel,
  testId = "progress-circle",
}: ProgressCircleProps) => {
  const sizeConfig = SIZE_CONFIG[size];
  const { diameter, strokeWidth, labelClassName } = sizeConfig;
  const radius = (diameter - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  const absValue = Math.round(Math.abs(value ?? 0));

  const dashOffset = circumference * (1 - absValue / 100);

  const shouldShowLabel = typeof showLabel === "boolean" ? showLabel : size !== ProgressCircleSize.XS;

  const colorClass = {
    [ProgressCircleColor.GREEN]: "text-green-500",
    [ProgressCircleColor.AMBER]: "text-amber-500",
    [ProgressCircleColor.RED]: "text-red-500",
    [ProgressCircleColor.PURPLE]: "text-marketing-purple-500",
  };

  return (
    <div
      role="progressbar"
      aria-valuemin={infinite ? -100 : 0}
      aria-valuemax={100}
      aria-valuenow={infinite ? -100 : Math.round(absValue)}
      aria-valuetext={`${Math.round(absValue)}${unit}`}
      aria-label={ariaLabel ?? `Progress ${absValue}${unit}`}
      className={"relative inline-flex select-none items-center justify-center"}
      style={{ width: diameter, height: diameter }}
      data-testid={testId}
    >
      <svg
        className="block h-full w-full -rotate-90"
        viewBox={`0 0 ${diameter} ${diameter}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          className="text-background-surface-neutral-subtle"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          cx={diameter / 2}
          cy={diameter / 2}
          r={radius}
          opacity={infinite ? 0.2 : 1}
        />

        {infinite ? (
          <circle
            className={clsx(colorClass[color])}
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={`${circumference * 0.3} ${circumference * 0.7}`}
            cx={diameter / 2}
            cy={diameter / 2}
            r={radius}
          >
            <animateTransform
              attributeName="transform"
              type="rotate"
              values={`0 ${diameter / 2} ${diameter / 2};360 ${diameter / 2} ${diameter / 2}`}
              dur="1.5s"
              repeatCount="indefinite"
            />
          </circle>
        ) : (
          <circle
            className={clsx(colorClass[color], "transition-[stroke-dashoffset] duration-300 ease-in-out")}
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            cx={diameter / 2}
            cy={diameter / 2}
            r={radius}
          />
        )}
      </svg>

      {shouldShowLabel ? (
        <span
          className={clsx(
            "absolute font-semibold text-text-base-primary left-[50%] translate-x-[-40%] top-[50%] translate-y-[-50%]",
            labelClassName ?? ""
          )}
        >{`${absValue}${unit}`}</span>
      ) : null}
    </div>
  );
};
